import {useRouter} from '@/hooks/useAppRouter';

import styles from './search-results-grid.module.css';

interface SearchResult {
    id: string;
    name: string;
    sku?: string;
    thumbnail?: string;
}

interface SearchResultsGridProps {
    results: SearchResult[];
    type: 'product' | 'group' | 'material';
}

export function SearchResultsGrid({results, type}: SearchResultsGridProps) {
    const router = useRouter();

    // Limit to max 6 cards
    const displayResults = results.slice(0, 6);

    const handleResultClick = (result: SearchResult) => {
        let path = '';
        switch (type) {
            case 'product':
                path = `/products/${result.id}/product-details`;
                break;
            case 'group':
                path = `/groups/${result.id}/group-details`;
                break;
            case 'material':
                path = `/materials/${result.id}/material-details`;
                break;
        }
        router.push(path);
    };

    // Handle drag start for products - allows dragging into editor
    const handleDragStart = (e: React.DragEvent, result: SearchResult) => {
        if (type !== 'product') return;

        const dragData = JSON.stringify({
            type: 'linda-product',
            productId: result.id,
            name: result.name,
            sku: result.sku,
            thumbnail: result.thumbnail,
        });

        // Set drag data with product info - use text/plain for better compatibility
        e.dataTransfer.setData('text/plain', dragData);
        e.dataTransfer.setData('application/json', dragData);
        e.dataTransfer.effectAllowed = 'copy';
    };

    // Determine grid class based on number of results
    const getGridClassName = () => {
        const count = displayResults.length;
        if (count === 1) return `${styles.grid} ${styles.gridSingle}`;
        if (count === 2) return `${styles.grid} ${styles.gridDouble}`;
        if (count === 3) return `${styles.grid} ${styles.gridTriple}`;
        if (count === 4) return `${styles.grid} ${styles.gridQuad}`;
        // 5-6 results use 3-column grid
        return `${styles.grid} ${styles.gridMulti}`;
    };

    if (results.length === 0) {
        return <div className={styles.emptyState}>No results found</div>;
    }

    return (
        <div className={getGridClassName()}>
            {displayResults.map((result, index) => (
                <div
                    key={`${result.id}-${index}`}
                    className={styles.resultCard}
                    onClick={() => handleResultClick(result)}
                    draggable={type === 'product'}
                    onDragStart={(e) => handleDragStart(e, result)}
                >
                    {result.thumbnail ? (
                        <div className={styles.imageContainer}>
                            <img
                                src={result.thumbnail}
                                alt={result.name}
                                className={styles.image}
                                loading="lazy"
                            />
                        </div>
                    ) : (
                        <div className={styles.imagePlaceholder}>No image</div>
                    )}
                    <div className={styles.resultInfo}>
                        <div className={styles.resultName}>{result.name}</div>
                    </div>
                    <div className={styles.hoverInfo}>
                        <div className={styles.hoverName}>{result.name}</div>
                        {result.sku && (
                            <div className={styles.hoverSku}>
                                SKU: {result.sku}
                            </div>
                        )}
                    </div>
                </div>
            ))}
        </div>
    );
}
